import { useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { observer } from "mobx-react-lite";
import Icon from "@mdi/react";
import { mdiArrowLeft, mdiChevronLeft, mdiChevronRight } from "@mdi/js";
import { Button } from "@heroui/react";
import { ROUTES, discountDetailPath } from "@/constants/routes";
import { discountsStore } from "@/stores/DiscountsStore";
import { formatMoneyFromKopecks } from "./discount-format";
import "./DiscountsPage.css";

const DiscountUsagesPage: React.FC = observer(() => {
  const { uuid } = useParams<{ uuid: string }>();
  const navigate = useNavigate();
  const store = discountsStore;

  useEffect(() => {
    if (!uuid) return;
    store.resetUsages();
    void store.loadUsages(uuid);
    return () => {
      store.resetUsages();
    };
  }, [uuid]);

  const meta = store.usagesMeta;
  const totalPages = meta?.totalPages ?? 1;

  const formatDateTime = (iso: string) => {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return iso;
    return d.toLocaleString("ru-RU");
  };

  return (
    <div className="dc-page" data-testid="discount-usages-page">
      <Button
        variant="ghost"
        className="dc-form-back"
        onPress={() =>
          uuid
            ? navigate(discountDetailPath(uuid))
            : navigate(ROUTES.DISCOUNTS)
        }
      >
        <Icon path={mdiArrowLeft} size={0.8} />
        Назад
      </Button>

      <h1 className="dc-page__title">
        Применения
        {store.usagesDiscountTitle ? `: ${store.usagesDiscountTitle}` : ""}
      </h1>

      <div className="dc-table-wrap" aria-busy={store.isUsagesLoading}>
        {store.usages.length === 0 && !store.isUsagesLoading ? (
          <div className="dc-empty">Применений пока нет</div>
        ) : (
          <table className="dc-table">
            <thead>
              <tr>
                <th>Клиент</th>
                <th>Заявка</th>
                <th>Время</th>
                <th>Сумма скидки</th>
              </tr>
            </thead>
            <tbody>
              {store.usages.map((row) => (
                <tr key={row.uuid}>
                  <td>{row.client_uuid}</td>
                  <td>{row.booking_uuid}</td>
                  <td>{formatDateTime(row.applied_at)}</td>
                  <td>{formatMoneyFromKopecks(row.discount_amount)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {uuid && totalPages > 1 ? (
        <div className="dc-pagination">
          <Button
            isIconOnly
            variant="tertiary"
            isDisabled={store.usagesPage <= 1 || store.isUsagesLoading}
            aria-label="Предыдущая страница"
            onPress={() => store.setUsagesPage(store.usagesPage - 1, uuid)}
          >
            <Icon path={mdiChevronLeft} size={0.85} />
          </Button>
          <span>
            {store.usagesPage} / {totalPages}
          </span>
          <Button
            isIconOnly
            variant="tertiary"
            isDisabled={
              store.usagesPage >= totalPages || store.isUsagesLoading
            }
            aria-label="Следующая страница"
            onPress={() => store.setUsagesPage(store.usagesPage + 1, uuid)}
          >
            <Icon path={mdiChevronRight} size={0.85} />
          </Button>
        </div>
      ) : null}
    </div>
  );
});

DiscountUsagesPage.displayName = "DiscountUsagesPage";
export default DiscountUsagesPage;
